/**
 * Hooks de permissões de exportação de dados
 * -----------------------------------------------------------------------------
 * - useMyExportPermissions: permissões efetivas do usuário logado (arquivo /
 *   e-mail) por módulo exportável. Administradores têm as duas liberadas.
 * - useProfileExportPermissions: leitura/gravação das permissões de um perfil
 *   (usado na tela Perfis de Permissão).
 *
 * IMPORTANTE: estas permissões NÃO substituem o controle de menus já existente.
 * A permissão de menu continua definindo SE o botão de exportar aparece; aqui
 * definimos apenas COMO o arquivo chega ao usuário.
 *
 * @version 1.0.0
 * RELEASE: 2026-09-07 v1.0.0 - Criação inicial.
 */

import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

const logger = createLogger('ExportPermissions');

/** Permissão de exportação de um módulo */
export interface ExportPermission {
  export_key: string;
  can_download: boolean;
  can_email: boolean;
}

/** Resultado das permissões do usuário logado */
export interface MyExportPermissions {
  loading: boolean;
  isAdmin: boolean;
  /** Retorna as permissões efetivas de um módulo exportável */
  getPermission: (exportKey: string) => { canDownload: boolean; canEmail: boolean };
}

/** Permissões de exportação do usuário logado */
export function useMyExportPermissions(): MyExportPermissions {
  const { currentTenant } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ['my-export-permissions', currentTenant?.id],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_my_export_permissions', {
        p_tenant_hint: currentTenant?.id || null,
      } as never);
      if (error) throw error;
      return (data || []) as Array<ExportPermission & { is_admin: boolean }>;
    },
    enabled: !!currentTenant?.id,
    staleTime: 5 * 60 * 1000,
  });

  const isAdmin = Boolean(data?.some((linha) => linha.is_admin));

  const getPermission = useCallback(
    (exportKey: string) => {
      if (isAdmin) return { canDownload: true, canEmail: true };
      const linha = data?.find((l) => l.export_key === exportKey);
      return {
        canDownload: Boolean(linha?.can_download),
        canEmail: Boolean(linha?.can_email),
      };
    },
    [data, isAdmin],
  );

  return { loading: isLoading, isAdmin, getPermission };
}

/** Permissões de exportação de um perfil (tela de Perfis de Permissão) */
export function useProfileExportPermissions(profileId: string | null) {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['profile-export-permissions', profileId, currentTenant?.id],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_export_permissions', {
        p_profile_id: profileId!,
        p_tenant_hint: currentTenant?.id || null,
      } as never);
      if (error) throw error;
      return (data || []) as ExportPermission[];
    },
    enabled: !!profileId && !!currentTenant?.id,
    staleTime: 60 * 1000,
  });

  /** Grava a lista completa de permissões do perfil */
  const save = useCallback(
    async (itens: ExportPermission[]) => {
      if (!profileId) return;
      const { error } = await supabase.rpc('set_export_permissions', {
        p_profile_id: profileId,
        p_items: itens,
        p_tenant_hint: currentTenant?.id || null,
      } as never);
      if (error) {
        logger.error('Erro ao salvar permissões de exportação:', error);
        throw error;
      }
      queryClient.invalidateQueries({ queryKey: ['profile-export-permissions', profileId] });
      queryClient.invalidateQueries({ queryKey: ['my-export-permissions'] });
    },
    [profileId, currentTenant?.id, queryClient],
  );

  return { permissions: query.data || [], isLoading: query.isLoading, save };
}
